import * as React from "react";
import type {
    IMegaMenuContext,
    IMegaMenuEntry,
    IMegaMenuPersistenceManager,
    IMegaMenuFavorite,
    IMegaMenuData
} from './definitions';
import type { ILocationMatchProp, ILocationProp } from '@@data/entities';
import { IMegaMenuRootEntryControlProps, MegaMenuRootEntryControl } from "./megamenuroot.item";
import { AnimatePresence, motion } from 'framer-motion';
import { MegaMenuPanel } from "./megamenupanel";
import type { IMegaMenuDataContext } from "./megamenudata";
import type { IAppShellMenuState } from "@@components/appshellmenucontext";
import { withI18nHelper } from "@inwink/i18n";
import { Entities } from "@inwink/entities/entities";
import { SlowFadeTransition } from "@@components/transitions";

import "./megamenuroot.less";

export interface IMegaMenuRootProps extends IMegaMenuContext {
    className?: string;
    i18nHelper?: Entities.i18nHelper;
    menuContext: IMegaMenuDataContext;
    location?: ILocationProp;
    match?: ILocationMatchProp;
    stamp?: any;
    persistence?: IMegaMenuPersistenceManager;
    menuState: IAppShellMenuState;
    onMenuItemClicked: () => void;
    onMenuEntryClicked: () => void;
}

export interface IMegaMenuRootState {
    initialized: boolean;
    expandedEntry?: IMegaMenuEntry;
    expandedEntryStamp?: string;
}

@withI18nHelper()
export class MegaMenuRoot extends React.Component<IMegaMenuRootProps, IMegaMenuRootState> {
    constructor(props: IMegaMenuRootProps) {
        super(props);

        this.state = {
            initialized: false
        };
    }

    componentDidMount(): void {
        this.setState({ initialized: true });
    }

    showPanel = (entry: IMegaMenuEntry) => {
        this.setState({
            expandedEntry: entry,
            expandedEntryStamp: entry?.key + Date.now().toString()
        }, () => {
            this.props.onMenuEntryClicked();
        });
    };

    render() {
        const classNames = ["megamenu-root"];

        if (this.props.className) {
            classNames.push(this.props.className);
        }

        if (this.props.menuState.collapsed) {
            classNames.push("menu-collapsed");
        }

        const items = this.props.menuContext?.config?.entries?.map((m, idx) => {
            const props: IMegaMenuRootEntryControlProps = {
                ...this.props,
                key: m.key || "item" + idx,
                dataManager: this.props.menuContext.datamgr,
                expanded: m === this.state.expandedEntry,
                onToggleFavorites: this.props.menuContext.toggleFavorites,
                onShowPanel: this.showPanel,
                entry: m
            };

            const component = m.rootComponent || MegaMenuRootEntryControl;

            return <SlowFadeTransition key={m.key || "item" + idx} in={this.state.initialized}>
                {React.createElement(component, props) as any}
            </SlowFadeTransition>;
        });

        let menuPanel = null;
        if (this.props.menuContext && this.props.isMobile && this.state.expandedEntry) {
            menuPanel = <MegaMenuRootMenuPanel
                {...this.props}
                dataManager={this.props.menuContext.datamgr}
                key={this.state.expandedEntryStamp}
                entry={this.state.expandedEntry}
                onToggleFavorites={this.props.menuContext.toggleFavorites}
                hide={() => {
                    this.setState({ expandedEntry: null });
                }}
                onMenuItemClicked={() => {
                    this.setState({ expandedEntry: null });
                    this.props.onMenuItemClicked();
                }}
            />;
        }

        return <div className={classNames.join(" ")}>
            {items}
            {menuPanel}
            {/* <SearchMenu i18nHelper={this.props.i18nHelper} /> */}
        </div>;
    }
}

interface IMegaMenuRootMenuPanelProps extends IMegaMenuRootProps {
    dataManager: IMegaMenuData;
    entry: IMegaMenuEntry;
    hide: () => void;
    onToggleFavorites: (items: IMegaMenuFavorite[]) => void;
}

interface IMegaMenuRootMenuPanelState {
    visible: boolean;
    menuClicked?: boolean;
}
class MegaMenuRootMenuPanel extends React.Component<IMegaMenuRootMenuPanelProps, IMegaMenuRootMenuPanelState> {
    constructor(props: IMegaMenuRootMenuPanelProps) {
        super(props);
        this.state = {
            visible: false
        };
    }

    componentDidMount(): void {
        this.setState({ visible: true });
    }

    render() {
        return (
            <AnimatePresence
                onExitComplete={() => {
                    if (this.state.menuClicked) {
                        this.props.onMenuItemClicked();
                    } else {
                        this.props.hide();
                    }
                }}
            >
                {this.state.visible && (
                    <motion.div 
                        key={this.props.entry.key}
                        className="megamenu-root-mobilepanel"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0, transition: { duration: 0.35 } }}
                        exit={{ opacity: 0, y: 50, transition: { duration: 0.25 } }}                        
                    >
                        <MegaMenuPanel {...this.props}
                            onBack={() => {
                                this.setState({ visible: false });
                            }}
                            onMenuItemClicked={() => {
                                this.setState({ visible: false, menuClicked: true });
                            }} />
                    </motion.div>
                )}
            </AnimatePresence>
        );
    }
}